import React, { useEffect, useState } from "react";
import { Copy, Facebook, MessageCircle, Share2, X } from "lucide-react";

export default function SharePanel({ open, onClose, currentChannel, currentVideo, apiBase, onShared }) {
  const [cards, setCards] = useState({ video: null, series: null, channel: null });
  const [target, setTarget] = useState("video");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !currentChannel || currentChannel.virtual) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setCopied(false);

    const channelQs = new URLSearchParams({ channel_id: currentChannel.id });
    const videoQs = new URLSearchParams({ channel_id: currentChannel.id });
    if (currentVideo?.id) videoQs.set("video_id", currentVideo.id);

    const getCard = async (qs) => {
      const r = await fetch(`${apiBase}/share/canonical?${qs.toString()}`, { signal: controller.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    };

    Promise.all([
      currentVideo?.id ? getCard(videoQs) : Promise.resolve(null),
      getCard(channelQs),
    ])
      .then(([video, channel]) => {
        const series = video?.series_url ? {
          title: video.series_title || currentVideo?.show_title || "Series",
          description: `Watch ${video.series_title || currentVideo?.show_title || "this series"} on scartlead.tv.`,
          url: video.series_url,
          image_url: video.image_url,
        } : null;
        setCards({ video, series, channel });
        setTarget(video ? "video" : "channel");
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          setCards({
            video: currentVideo?.id ? {
              title: currentVideo?.title || "scartlead.tv",
              description: currentVideo?.description || "",
              url: window.location.href,
              image_url: "/og-card.png",
            } : null,
            series: null,
            channel: {
              title: currentChannel?.name || "scartlead.tv",
              description: currentChannel?.description || "",
              url: window.location.href,
              image_url: "/og-card.png",
            },
          });
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [open, currentChannel, currentVideo, apiBase]);

  if (!open) return null;

  const card = target === "channel"
    ? cards.channel
    : target === "series"
      ? (cards.series || cards.video || cards.channel)
      : (cards.video || cards.channel);
  const url = card?.url || window.location.href;
  const title = card?.title || "scartlead.tv";
  const description = card?.description || "Tune in to scartlead.tv.";
  const image = card?.image_url || "/og-card.png";
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(`${title}\n${description}`);
  const markShared = () => onShared && onShared();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
      markShared();
    } catch (_) {}
  };

  const popup = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=720,height=640");
    markShared();
  };

  const more = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url });
        markShared();
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    await copy();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="share-panel">
      <div className="w-full max-w-[620px] rounded-xl border border-white/15 bg-[#090b0c] overflow-hidden" style={{ fontFamily: "VT323, monospace" }}>
        <div className="flex justify-between items-center px-4 py-3 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[.3em] text-white/35">scartlead.tv TRANSMISSION DESK</div>
            <div className="text-xl tracking-[.13em] text-[var(--phosphor)]">SHARE THIS BROADCAST</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 border border-white/10 rounded text-white/50 flex items-center justify-center" aria-label="Close share panel"><X size={17} /></button>
        </div>
        <div className="p-4">
          {cards.video && cards.channel && (
            <div className={`mb-3 grid ${cards.series ? "grid-cols-3" : "grid-cols-2"} gap-2`} data-testid="share-target-picker">
              <button type="button" onClick={() => setTarget("video")} data-testid="share-episode"
                className={`min-h-11 rounded border px-3 text-[12px] tracking-[.16em] ${target === "video" ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-white/5" : "border-white/10 text-white/45"}`}>
                SHARE EPISODE / TITLE
              </button>
              {cards.series && (
                <button type="button" onClick={() => setTarget("series")} data-testid="share-series"
                  className={`min-h-11 rounded border px-3 text-[12px] tracking-[.16em] ${target === "series" ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-white/5" : "border-white/10 text-white/45"}`}>
                  SHARE SERIES
                </button>
              )}
              <button type="button" onClick={() => setTarget("channel")} data-testid="share-channel"
                className={`min-h-11 rounded border px-3 text-[12px] tracking-[.16em] ${target === "channel" ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-white/5" : "border-white/10 text-white/45"}`}>
                SHARE CHANNEL
              </button>
            </div>
          )}
          <div className="relative aspect-[1200/630] rounded-lg overflow-hidden border border-white/10 bg-black">
            <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.src = "/og-card.png"; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/10" />
            <div className="absolute bottom-0 inset-x-0 p-5">
              <div className="text-[10px] tracking-[.28em] text-[var(--phosphor)]">
                {target === "channel"
                  ? "● LIVE CHANNEL ON scartlead.tv"
                  : target === "series"
                    ? "● COMPLETE SERIES ON scartlead.tv"
                    : "● NOW SHOWING ON scartlead.tv"}
              </div>
              <div className="mt-1 text-3xl text-white">{title}</div>
              <div className="mt-2 text-xs text-white/70 line-clamp-2">{description}</div>
            </div>
          </div>
          <div className="mt-3 border border-white/10 rounded bg-black/35 px-3 py-2">
            <div className="text-[9px] tracking-[.22em] text-white/30">SHORT LINK</div>
            <div className="mt-1 truncate text-[13px] text-[var(--phosphor)]">{loading ? "GENERATING SHORT LINK…" : url}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
            <button onClick={copy} disabled={!card || loading} className="share-desk-button"><Copy size={16} />{copied ? "COPIED" : "COPY LINK"}</button>
            <button onClick={() => popup(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)} disabled={!card || loading} className="share-desk-button"><Facebook size={16} />FACEBOOK</button>
            <button onClick={() => popup(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`)} disabled={!card || loading} className="share-desk-button"><span className="text-base">𝕏</span>X</button>
            <button onClick={() => popup(`https://wa.me/?text=${encodedText}%0A${encodedUrl}`)} disabled={!card || loading} className="share-desk-button"><MessageCircle size={16} />WHATSAPP</button>
            <button onClick={more} disabled={!card || loading} className="share-desk-button"><Share2 size={16} />MORE APPS</button>
          </div>
        </div>
      </div>
    </div>
  );
}
